import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  BookOpen, 
  Shield,
  GraduationCap,
  PhoneOff
} from 'lucide-react';

export default function HomePage() {
  return (
    <div>
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-[85vh] flex items-center pt-20 bg-slate-50"
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Hagen-Family-Foundation
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
                Supporting families and communities with tools that prevent scams 
                and protect the vulnerable.
              </p>

              <p className="text-slate-600 leading-relaxed max-w-xl">
                The Hagen-Family-Foundation is dedicated to helping young people attain a 
                higher education for a brighter future. We're currently working toward 
                501(c)(3) nonprofit status as we formalize our mission to serve communities 
                in the Philippines and beyond.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to="/initiatives"
                  data-testid="hero-initiatives"
                  className="inline-flex items-center justify-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-md px-6 py-3 font-medium transition-colors shadow-sm"
                >
                  Explore Our Initiatives
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/mission"
                  data-testid="hero-learn-more"
                  className="inline-flex items-center justify-center gap-2 bg-white text-slate-700 hover:bg-slate-100 border border-slate-300 rounded-md px-6 py-3 font-medium transition-colors"
                >
                  Learn Our Mission
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block">
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=800&q=80"
                  alt="Students learning together"
                  className="w-full h-[450px] object-cover"
                />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-5 max-w-[260px] border border-slate-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-blue-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Education First</p>
                    <p className="text-xs text-slate-500">Tacloban City, PH</p>
                  </div>
                </div>
                <p className="text-xs text-slate-600">
                  Supporting students with scholarships and mentorship programs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Overview */}
      <section data-testid="mission-section" className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="inline-block text-blue-600 font-medium text-sm tracking-wide uppercase mb-3">
              Our Purpose
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Building Brighter Futures
            </h2>
            <p className="text-lg text-slate-600">
              The Hagen-Family-Foundation is dedicated to two core missions: providing educational 
              assistance to young people and protecting families from scams and fraud.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Education Card */}
            <div 
              data-testid="focus-education"
              className="bg-white border border-slate-200 rounded-lg p-7 hover:shadow-lg transition-shadow"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-5">
                <BookOpen className="w-7 h-7 text-blue-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Educational Support
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Providing scholarships and resources to students pursuing higher education, 
                ensuring financial barriers don't stand in the way of dreams.
              </p>
            </div>

            {/* Career Card */}
            <div 
              data-testid="focus-career"
              className="bg-white border border-slate-200 rounded-lg p-7 hover:shadow-lg transition-shadow"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-5">
                <GraduationCap className="w-7 h-7 text-blue-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Career Development
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Fostering pathways to meaningful careers that provide for families 
                and contribute to the economic vitality of communities.
              </p>
            </div>

            {/* Protection Card */}
            <div 
              data-testid="focus-protection"
              className="bg-white border border-slate-200 rounded-lg p-7 hover:shadow-lg transition-shadow"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-5">
                <Shield className="w-7 h-7 text-blue-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Scam Protection
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Educating and protecting communities from phone, text, and email scams 
                through awareness programs and technological solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Current Initiative Highlight */}
      <section data-testid="projects-section" className="py-20 bg-slate-800">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block text-blue-400 font-medium text-sm tracking-wide uppercase mb-3">
              Active Initiative
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              NoMoreScamCalls
            </h2>
            <p className="text-lg text-slate-300">
              Our flagship project protecting families from phone scams.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div 
              data-testid="project-nomoresscamcalls"
              className="bg-slate-700 border border-slate-600 rounded-lg p-8"
            >
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 bg-blue-600 rounded-lg flex items-center justify-center">
                  <PhoneOff className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">NoMoreScamCalls</h3>
                  <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">Active</span>
                </div>
              </div>
              <p className="text-slate-300 leading-relaxed mb-6">
                Protecting families from phone scams through education, awareness campaigns, 
                and technological solutions that identify and block fraudulent callers. 
                This is our primary revenue-generating initiative that supports the foundation's 
                educational mission.
              </p>
              <a 
                href="#" 
                className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors"
              >
                Learn more <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/initiatives"
              data-testid="view-all-initiatives"
              className="inline-flex items-center gap-2 bg-white text-slate-800 hover:bg-slate-100 rounded-md px-6 py-3 font-medium transition-colors"
            >
              View All Initiatives
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Regional Focus */}
      <section data-testid="regional-section" className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1518509562904-e7ef99cdcc86?w=800&q=80"
                  alt="Philippines landscape"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>

            <div className="space-y-5">
              <span className="inline-block text-blue-600 font-medium text-sm tracking-wide uppercase">
                Regional Focus
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                Starting in the Philippines
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Our educational programs begin in the Tacloban City area, with plans to expand 
                throughout the Philippines. We're committed to bringing opportunity where the 
                need is greatest.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Through education, we open doors to careers that transform lives and create 
                lasting positive change for families and communities.
              </p>
              <Link
                to="/mission"
                data-testid="learn-more-regional"
                className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 font-medium transition-colors"
              >
                Learn more about our mission
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-20 bg-blue-700">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Join Our Journey
          </h2>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto mb-8">
            Whether through partnership, support, or spreading our message, 
            there are many ways to help us create educational opportunities 
            and protect families from fraud.
          </p>
          <Link
            to="/contact"
            data-testid="cta-connect"
            className="inline-flex items-center gap-2 bg-white text-blue-700 hover:bg-slate-100 rounded-md px-8 py-4 font-semibold transition-colors shadow-sm"
          >
            Connect With Us
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
